<template>
  <div class="login-register-area">
    <div
      v-if="compIsLoading || googleInfo.hasHash !== false"
      class="loading"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>
    <template v-else>
      <header>
        <span class="login-register-area__title">
          {{ titles[action] }}
        </span>
      </header>
      <section>
        <v-form ref="form">
          <div
            v-if="['login', 'register', 'forgotPassword'].includes(action)"
            class="item flex-item stretch"
          >
            <span class="label">手機號碼</span>
            <v-text-field
              ref="phone"
              class="input"
              label="請輸入手機號碼"
              single-line
              outlined
              :autofocus="hasPhoneField"
              :value="inputData.phone"
              type="tel"
              hide-details="auto"
              maxlength="10"
              :rules="inputRules.phoneRule"
              :disabled="!hasPhoneField"
              @input="inputData.phone = full2half($event)"
              @keypress.enter.prevent="submit"
            />
          </div>
          <div
            v-if="action === 'login'"
            class="item"
          >
            <span class="label">密碼</span>
            <v-text-field
              ref="password"
              v-model="inputData.password"
              class="input"
              label="請輸入您的密碼"
              single-line
              outlined
              autofocus
              hide-details="auto"
              maxlength="12"
              :rules="inputRules.passwordRule"
              :append-icon="visible.password ? icons.mdiEye : icons.mdiEyeOff"
              :type="visible.password ? 'text' : 'password'"
              @keypress.enter.prevent="submit"
              @click:append="visible.password = !visible.password"
            />
            <div class="forget-container">
              <span
                class="forget"
                @click="action = 'forgotPassword'"
              ><nuxt-link :to="{name: 'auth-action', params: {action: 'forgotPassword'}}">忘記密碼 ?</nuxt-link></span>
            </div>
          </div>
          <div
            v-if="['register', 'forgotPassword'].includes(action)"
            class="item flex-item stretch verifyCode"
          >
            <span class="label">驗證碼</span>
            <v-text-field
              ref="verifyCode"
              v-model="inputData.verifyCode"
              class="input"
              label="請輸入簡訊驗證碼"
              single-line
              outlined
              type="text"
              hide-details="auto"
              maxlength="6"
              :rules="inputRules.verifyCodeRule"
              @keypress.enter.prevent="submit"
            />
            <v-btn
              class="btn"
              :class="{'is-sending': verification.isCountingDown}"
              outlined
              color="primary"
              :loading="verification.isSending"
              :disabled="verification.isCountingDown"
              @click="clickSendVerification"
            >
              {{
                verification.isCountingDown ?
                  `重新取得驗證碼(${verification.countDown})` :
                  '發送驗證碼'
              }}
            </v-btn>
          </div>

          <div
            v-if="showSetPassword"
            class="item flex-item"
          >
            <span class="label">新密碼</span>
            <v-text-field
              ref="newPassword"
              v-model="inputData.newPassword"
              class="input"
              label="請設定含英數8-12位數的密碼"
              single-line
              outlined
              autofocus
              hide-details="auto"
              maxlength="12"
              :rules="inputRules.passwordRule"
              :append-icon="visible.newPassword ? icons.mdiEye : icons.mdiEyeOff"
              :type="visible.newPassword ? 'text' : 'password'"
              @keypress.enter.prevent="submit"
              @click:append="visible.newPassword = !visible.newPassword"
            />
          </div>

          <div
            v-if="showSetPassword"
            class="item flex-item"
          >
            <span class="label">確認密碼</span>
            <v-text-field
              ref="newPassowrdAgain"
              v-model="inputData.newPassowrdAgain"
              class="input"
              label="請再次輸入密碼"
              single-line
              outlined
              autofocus
              hide-details="auto"
              maxlength="12"
              :rules="inputRules.newPasswordAgainRule"
              :append-icon="visible.newPasswordAgain ? icons.mdiEye : icons.mdiEyeOff"
              :type="visible.newPasswordAgain ? 'text' : 'password'"
              @keypress.enter.prevent="submit"
              @click:append="visible.newPasswordAgain = !visible.newPasswordAgain"
            />
          </div>
          <div class="item flex-item">
            <v-btn
              color="primary"
              class="submit-btn"
              :loading="submitIsLoading"
              :disabled="submitIsDisabled"
              @click="submit"
            >
              {{ submitText[action] }}
            </v-btn>
          </div>
        </v-form>
      </section>
      <footer>
        <div
          v-if="action === 'login'"
          class="quick-login"
        >
          <div class="quick-login__divider">
            <div class="quick-login__divider-text">
              <span>或透過以下方式快速登入</span>
            </div>
          </div>
          <v-btn
            class="quick-login__btn apple"
            outlined
            :loading="googleInfo.isLoading"
            @click="appleLogin"
          >
            <img
              src="~/assets/image/social-apple.png"
              alt="apple-icon"
            >
            <span class="hide-mobile">Apple</span>
          </v-btn>
          <v-btn
            v-if="!$store.state.isApp"
            class="quick-login__btn google"
            color="black"
            outlined
            :loading="googleInfo.isLoading"
            @click="googleLogin"
          >
            <img
              src="~/assets/image/google.svg"
              alt="google-icon"
            >
            <span class="hide-mobile">Google</span>
          </v-btn>
          <p
            v-show="googleInfo.canLogin === false"
            class="quick-login__cookie-info"
          >
            請確認允許開啟第三方 Cookie<br>
            未開啟的話，網站的功能可能無法正常運作。
          </p>
          <p class="quick-login__notice">
            登入手機會員，完成社群帳號連結即可啟用快速登入
          </p>
        </div>
        <div
          v-if="action === 'login'"
          class="login-notice"
        >
          <p>
            還不是會員嗎？ <nuxt-link :to="{name: 'auth-action', params: {action: 'register'}}">
              立即前往註冊
            </nuxt-link>
          </p>
        </div>
        <div
          v-if="['register', 'forgotPassword'].includes(action)"
          class="login-notice"
        >
          <p>
            已經是會員了嗎？ <nuxt-link :to="{name: 'auth-action', params: {action: 'login'}}">
              登入
            </nuxt-link>
          </p>
        </div>
      </footer>
    </template>

    <v-dialog
      ref="alertDialog"
      v-model="alertDialog.open"
      width="500"
    >
      <v-card>
        <v-card-title
          v-show="alertDialog.title"
          class="headline"
        >
          {{ alertDialog.title }}
        </v-card-title>
        <v-card-text v-show="alertDialog.message">
          {{ alertDialog.message }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <template v-if="alertDialog.btn.length > 0">
            <v-btn
              v-for="(btn, index) in alertDialog.btn"
              :key="index"
              :ref="'alertDialogBtn-' + index"
              :color="btn.btnColor"
              :outlined="btn.outlined"
              :depressed="btn.depressed"
              :loading="btn.btnIsLoading"
              @click="btn.btnClick"
            >
              {{ btn.btnText }}
            </v-btn>
          </template>
          <v-btn
            v-else
            ref="dialogBtn"
            color="primary"
            :autofocus="!alertDialog.open"
            :loading="alertDialog.loading"
            @click="alertDialog.donFun"
          >
            關閉
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import { mdiEye, mdiEyeOff } from '@mdi/js'
import guestPurchase from '@/mixin/guestPurchase.js'
import { phoneValidator } from '../utils/validations/phoneValidator'

export default {
  mixins: [guestPurchase],
  props: {
    propAction: {
      type: String,
      default: 'login'
    }
  },
  data () {
    return {
      action: this.propAction,
      icons: {
        mdiEye,
        mdiEyeOff
      },
      title: '',
      inputData: {
        phone: '',
        password: '',
        verifyCode: '',
        newPassword: '',
        newPasswordAgain: ''
      },
      visible: {
        password: false,
        newPassword: false,
        newPasswordAgain: false
      },
      inputRules: {
        phoneRule: [],
        passwordRule: [],
        newPasswordAgainRule: [],
        verifyCodeRule: []
      },
      optionalRules: {
        phone: {
          invalidPhone: null
        },
        verifyCode: {
          invalidCode: null,
          alwaysOn: false // 驗證達上限
        }
      },
      verification: {
        isCountingDown: false,
        isSending: false,
        cdSecTime: 180,
        countDown: 0,
        token: '',
        jwt: ''
      },
      googleInfo: {
        isLoading: null,
        canLogin: null,
        hashParams: {},
        hasHash: null
      },
      alertDialog: {
        open: false,
        title: '',
        message: '',
        loading: false,
        donFun: () => {},
        btn: []
      },
      compIsLoading: false,
      submitIsLoading: false,
      submitIsDisabled: false,
      titles: {
        login: '會員登入',
        register: '會員註冊',
        forgotPassword: '忘記密碼',
        setPassword: '設定密碼'
      },
      submitText: {
        login: '登入',
        register: '驗證',
        forgotPassword: '驗證',
        registerSetPassword: '確認',
        forgotSetPassword: '確認'
      },
      appleSignInHandlers: { // Added to store references for proper event cleanup
        success: null,
        failure: null
      }
    }
  },
  computed: {
    ...mapGetters(['isGuestAgent']),
    hasPhoneField () {
      return ['login', 'register', 'forgotPassword'].includes(this.action)
    },
    showSetPassword () {
      return ['registerSetPassword', 'forgotSetPassword'].includes(this.action)
    }
  },
  async mounted () {
    this.initializeAppleSignIn()
    this.setupAppleSignInListeners() // Updated call
    if (this.$nuxt.$route && this.$nuxt.$route.hash) {
      this.loading = true
      this.googleInfo.hasHash = this.$nuxt.$route.hash.indexOf('google') > -1 // google登入導向回來
    } else {
      this.googleInfo.hasHash = false
    }
    if (this.$nuxt.$route.hash) {
      const regex = /([^&=]+)=([^&]*)/g
      let m = regex.exec(this.$nuxt.$route.hash)
      while (m) {
        this.googleInfo.hashParams[decodeURIComponent(m[1])] = decodeURIComponent(m[2])
        m = regex.exec(this.$nuxt.$route.hash)
      }
    }
    if (process.client && this.googleInfo.hashParams && this.googleInfo.hashParams.access_token) {
      this.$auth.$storage.setLocalStorage('googleToken', this.googleInfo.hashParams.access_token)
    }
    this.checkGoogle()
    if (this.$nuxt.context.env.NODE_ENV_NAME !== 'ec') {
      this.verification.cdSecTime = 60
    }
  },
  beforeDestroy () {
    this.removeAppleSignInListeners()
  },

  methods: {
    setupAppleSignInListeners () {
      // Define the handler functions and store
      this.appleSignInHandlers.success = async (event) => {
        // Handle successful response.
        this.compIsLoading = true
        try {
          const { authorization } = event.detail
          const routeData = this.$nuxt.$router.resolve({
            name: 'login'
          })
          const appleData = await this.$serviceApi.post('/serviceApi/appleAfterSignin', {
            code: authorization.code,
            state: authorization.state,
            redirectUri: location.origin + routeData.resolved.fullPath
          })
          // 登入
          await this.$store.dispatch('thirdLogin', {
            UserType: 'KingnetApple',
            Account: appleData.data.sub,
            IdToken: appleData.data.id_token,
            UserId: appleData.data.sub
          })
          window.localStorage.setItem(
            'memberUpgradeAlertInfo',
            JSON.stringify({
              time: null,
              open: false
            })
          )
          this.$gaLogEvent('登入', 'Apple_成功')
          this.$emit('isLogin')
        } catch (error) {
          this.compIsLoading = false
          this.$gaLogEvent('登入', 'Apple_失敗')
          console.error('Apple Sign In Error:', error)
        }
      }

      this.appleSignInHandlers.failure = (event) => {
        // Handle error.
        this.$store.dispatch('toast', {
          isOpen: true,
          text: 'Apple 登入失敗',
          type: 'error'
        })
      }

      // Add the event listeners using the stored functions
      document.addEventListener('AppleIDSignInOnSuccess', this.appleSignInHandlers.success)
      document.addEventListener('AppleIDSignInOnFailure', this.appleSignInHandlers.failure)
    },
    removeAppleSignInListeners () {
      if (this.appleSignInHandlers.success) {
        document.removeEventListener('AppleIDSignInOnSuccess', this.appleSignInHandlers.success)
      }
      if (this.appleSignInHandlers.failure) {
        document.removeEventListener('AppleIDSignInOnFailure', this.appleSignInHandlers.failure)
      }
    },
    full2half (val) {
      if (!val) return ''
      let temp = ''
      for (let i = 0; i < val.toString().length; i++) {
        let charCode = val.toString().charCodeAt(i)
        if (charCode >= 65281 && charCode <= 65374) {
          charCode -= 65248
        } else if (charCode === 12288) { // 全形空白轉半形
          charCode = 32
        }
        temp += String.fromCharCode(charCode)
      }
      return temp
    },
    async clickSendVerification () {
      this.optionalRules.verifyCode.invalidCode = null
      await this.setInputRules({ skipVerifyCode: true })
      this.$refs.form.validate()
      if (this.verification.isCountingDown) return
      if (!this.$refs.phone.validate()) return
      this.verification.isSending = true
      this.sendVerification(this.action)
    },
    async sendVerification (type) {
      if (type === 'forgotPassword') {
        const phoneExist = await this.checkPhoneExist()
        if (!phoneExist) {
          this.optionalRules.phone.invalidPhone = { value: this.inputData.phone, msg: '您輸入的手機號碼不存在，請點擊下方進行註冊' }
          this.submitIsLoading = false
          await this.setInputRules({ skipVerifyCode: true })
          this.$refs.form.validate()
          this.verification.isSending = false
          return
        }
      }
      const api = type === 'register' ? this.apiSendRegisterVeriCode : this.apiReSetPasswordVeriCode
      const logMsg = type === 'register' ? '註冊頁_click_發送驗證碼' : '忘記密碼頁_click_發送驗證碼'
      try {
        const { data } = await api()
        this.verification.token = data.Data.Token
        this.$gaLogEvent('發送驗證碼', logMsg)
        // 已發驗證碼
        this.$store.dispatch('toast', {
          isOpen: true,
          text: '已發送驗證碼',
          type: 'success'
        })
        this.startVerificationCountDown()
        this.submitIsDisabled = false
        this.verification.isSending = false
      } catch (error) {
        this.verification.isSending = false
      }
    },
    /** 驗證碼重新寄送計數器 */
    countDown () {
      if (this.verification.countDown <= 0) {
        this.verification.isCountingDown = false
        return
      }
      setTimeout(() => {
        this.verification.countDown--
        this.countDown()
      }, 1000)
    },
    startVerificationCountDown () {
      this.verification.countDown = this.verification.cdSecTime
      this.verification.isCountingDown = true
      this.countDown()
    },
    async submit () {
      await this.setInputRules()
      if (!this.$refs.form.validate()) return
      this.submitIsLoading = true
      switch (this.action) {
        case 'login':
          this.login()
          this.$FBConversionAPI({ event_name: '會員確定登入', user_data: { phone: this.inputData.phone } })
          break
        case 'register':
          this.register()
          break
        case 'forgotPassword':
          this.forgotPassword()
          break
        case 'registerSetPassword':
        case 'forgotSetPassword':
          this.setPassword()
          break
      }
    },
    async checkPhoneExist () {
      try {
        const result = await this.$axios.get('/api/KingnetAccount/CheckIsGuestMemberByPhone', { params: { Phone: this.inputData.phone } })
        if (result.data.Data.IsGuestMember) {
          return true
        }

        const checkIsKingnetMember = await this.$axios.get('/api/Account/CheckIsKingnetMemberByPhone', { params: { Phone: this.inputData.phone } })
        if (checkIsKingnetMember.data) {
          return !!checkIsKingnetMember.data.Data
        }
      } catch (error) {
        if (error.response.data.ErrorMessage &&
            error.response.data.ErrorMessage.Code === 403000) {
          // 電話錯誤！系統已經綁定過此電話
          return true
        }
        return false
      }
    },
    async forgotPassword () {
      if (!this.$refs.form.validate()) {
        this.submitIsLoading = false
        return Promise.reject(new Error('Form invalid'))
      }

      this.$axios.setHeader('RegisterVerificationToken', this.verification.token)
      const { data } = await this.$axios.post('/api/KingnetAccount/VerifictionReSetPasswordCode', { Code: this.inputData.verifyCode })
        .catch(async error => {
          this.verifyCodeErrorHandler(error)
          this.$axios.setHeader('RegisterVerificationToken', undefined)
          this.submitIsLoading = false
          return Promise.reject(error)
        })
      this.verification.token = data.Data.Token
      this.$axios.setHeader('RegisterVerificationToken', false)
      this.inputData.verifyCode = ''
      this.action = 'forgotSetPassword'
      this.submitIsLoading = false
    },
    async setInputRules (skipOptions) {
      await this.$set(this.inputRules, 'passwordRule', [
        val => !!val || '請輸入密碼',
        value => (String(value).length >= 8 && String(value).length <= 12) || '密碼須為英數8至12位數',
        value => {
          const pattern = /^[\d|a-zA-Z]+$/
          return pattern.test(String(value).toLowerCase()) || '密碼請勿包含特殊字符（如 @, #, $）'
        }
      ])
      await this.$set(this.inputRules, 'newPasswordAgainRule', [
        value => !!value || '請再輸入一次密碼',
        value => value === this.inputData.newPassword || '兩次密碼不相同'
      ])
      const verifyCodeRules = []
      if (!(skipOptions && skipOptions.skipVerifyCode)) {
        verifyCodeRules.push(value => !!value || '請輸入驗證碼')
        if (this.optionalRules.verifyCode) {
          if (this.optionalRules.verifyCode.invalidCode) {
            const { value, msg } = this.optionalRules.verifyCode.invalidCode
            verifyCodeRules.push(input => Number(input) !== Number(value) || msg)
          }
          if (this.optionalRules.verifyCode.alwaysOn) {
            verifyCodeRules.push(_ => '驗證碼失敗達上限')
          }
        }
      }

      const phoneRules = [val => !!val || '請輸入手機號碼', phoneValidator]

      if (!(skipOptions && skipOptions.skipInvalidPhone)) {
        if (this.optionalRules.phone) {
          if (this.optionalRules.phone.invalidPhone) {
            const { value, msg } = this.optionalRules.phone.invalidPhone
            phoneRules.push(input => input !== value || msg)
          }
        }
      }
      await this.$set(this.inputRules, 'verifyCodeRule', verifyCodeRules)
      await this.$set(this.inputRules, 'phoneRule', phoneRules)
    },
    async setInvalidVerifyCode (invalidCode, msg) {
      await this.$set(this.inputRules, 'verifyCodeRule', [value => !!value || '請輸入驗證碼', value => Number(value) !== Number(invalidCode) || msg])
      if (!this.optionalRules.verifyCode.invalidCode) {
        this.optionalRules.verifyCode.invalidCode = {}
      }
      this.optionalRules.verifyCode.invalidCode.value = invalidCode
      this.optionalRules.verifyCode.invalidCode.msg = msg
    },
    /** 手機+密碼 登入 */
    async login () {
      const phoneExist = await this.checkPhoneExist()
      if (!phoneExist) {
        this.optionalRules.phone.invalidPhone = { value: this.inputData.phone, msg: '您輸入的手機號碼不存在，請點擊下方進行註冊' }
        this.submitIsLoading = false
        await this.setInputRules()
        this.$refs.form.validate()
        return
      }
      await this.$store.dispatch('login', {
        Account: this.inputData.phone,
        Password: this.inputData.password,
        DeviceCode: this.$store.state.deviceCode || ''
      }).catch(error => {
        const errorObj = {
          isError: false,
          errorMessage: ''
        }
        switch (error.Code) {
          case 400001: // Password 不得為空。
            errorObj.isError = true
            errorObj.errorMessage = '密碼不得為空。'
            break
          case 401000: // 帳號或密碼錯誤
            errorObj.isError = true
            errorObj.errorMessage = '密碼錯誤'
            break
          default:
            break
        }
        if (errorObj.isError) {
          this.$store.dispatch('toast', {
            isOpen: true,
            text: errorObj.errorMessage,
            type: 'error'
          })
        }
        this.submitIsLoading = false
        this.$gaLogEvent('登入', '手機_失敗')
        return Promise.reject(error)
      })
      window.localStorage.setItem(
        'memberUpgradeAlertInfo',
        JSON.stringify({
          time: null,
          open: false
        })
      )
      // await this.$auth.fetchUser()
      this.submitIsLoading = false
      this.$gaLogEvent('登入', '手機_成功')
      this.$emit('isLogin')
    },
    /** 註冊 手機+驗證Token+驗證碼 */
    async register () {
      if (!this.$refs.form.validate()) return
      this.submitIsLoading = true
      this.$axios.setHeader('RegisterVerificationToken', this.verification.token)
      const { data } = await this.$axios.post('/api/KingnetAccount/VerifictionCode', { Code: this.inputData.verifyCode })
        .catch(async error => {
          this.$gaLogEvent('註冊', '註冊_失敗')
          await this.verifyCodeErrorHandler(error)
          if (error.errorCode === 400008) { // 驗證超過三次
            this.inputData.token = ''
          }
          this.$axios.setHeader('RegisterVerificationToken', false)
          this.submitIsLoading = false
          return Promise.reject(error)
        })
      this.$gaLogEvent('註冊', '註冊_成功')
      this.verification.token = data.Data.Token
      this.$axios.setHeader('RegisterVerificationToken', data.Data.Token)
      this.submitIsLoading = false
      this.action = 'registerSetPassword'
    },
    async verifyCodeErrorHandler (error) {
      const errorObj = error.response.data.ErrorMessage || {}
      const errorCode = errorObj.Code || null
      const errorReturnObj = {
        isError: false,
        message: '',
        donFun: () => {}
      }
      switch (errorCode) {
        case 400005: // 驗證碼不符
          await this.setInvalidVerifyCode(this.inputData.verifyCode, '驗證碼不正確，請重新輸入')
          this.$refs.form.validate()
          break
        case 401004: // 驗證超時
          await this.setInvalidVerifyCode(this.inputData.verifyCode, '驗證碼無效，請點擊重新取得驗證碼')
          this.$refs.form.validate()
          break
        case 400008: // 驗證碼失敗達上限
          this.optionalRules.verifyCode.invalidCode = null
          this.optionalRules.verifyCode.alwaysOn = true
          await this.$set(this.inputRules, 'verifyCodeRule', [_ => '驗證碼失敗達上限'])
          this.$refs.form.validate()
          break
        case 400013: // 驗證超過三次
          errorReturnObj.isError = true
          errorReturnObj.message = '今日已達3次驗證上限'
          this.submitIsDisabled = true
          break
        default:
          errorReturnObj.isError = true
          errorReturnObj.message = '驗證碼不正確，請重新輸入'
          break
      }
      if (errorReturnObj.isError) {
        this.$store.dispatch('toast', {
          isOpen: true,
          text: errorReturnObj.message,
          type: 'error'
        })
      }
      try {
        this.verification.token = error.response.data.Data.Token
      } catch (error) {
        this.verification.token = ''
      }
    },
    /** 設定密碼 新密碼+確認密碼 */
    async setPassword () {
      if (!this.$refs.form.validate()) {
        this.submitIsLoading = false
        return Promise.reject(new Error('Form invalid'))
      }

      // "註冊" 與 "忘記密碼" 共用設定表單 (新密碼, 確認密碼)
      // api 與行為不同 故分開處理
      await (
        (this.action === 'forgotSetPassword')
          ? this.apiForgotSetPassword()
          : this.apiRegisterSetPassword()
      ).catch(error => {
        return Promise.reject(error)
      })

      this.submitIsLoading = false
      this.$axios.setHeader('RegisterVerificationToken', false)
      await this.$store.dispatch('login', {
        Account: this.inputData.phone,
        Password: this.inputData.newPassword
      }).catch(error => {
        return Promise.reject(error)
      })
      window.localStorage.setItem(
        'memberUpgradeAlertInfo',
        JSON.stringify({
          time: null,
          open: false
        })
      )
      this.submitIsLoading = false
      this.$emit('isLogin')
    },
    async apiRegisterSetPassword () {
      this.$axios.setHeader('RegisterVerificationToken', this.verification.token)
      await this.$axios.post('/api/KingnetAccount/SetPasswordByServiceAccountTransfer', { Password: this.inputData.newPassword })
        .catch(error => {
          this.$store.dispatch('toast', {
            isOpen: true,
            text: '密碼設定失敗',
            type: 'error'
          })
          this.submitIsLoading = false
          return Promise.reject(error)
        })
    },
    async apiForgotSetPassword () {
      this.$axios.setHeader('RegisterVerificationToken', this.verification.token)
      await this.$axios.post('/api/KingnetAccount/ReNewPassword', { Password: this.inputData.newPassword })
        .catch(error => {
          this.$store.dispatch('toast', {
            isOpen: true,
            text: '密碼設定失敗',
            type: 'error'
          })
          this.submitIsLoading = false
          return Promise.reject(error)
        })
    },
    async googleLogin () {
      if (process.client && this.$auth.$storage.getState('toRoute')) {
        sessionStorage.setItem('toRoute', JSON.stringify(this.$auth.$storage.getState('toRoute')))
      }
      const clientId = '716724447213-tnuoobvaohn4enqvscur6ncr39me9f78.apps.googleusercontent.com'
      const redirectUri = window.location.origin + '/login'
      const scope = 'openid profile email'
      const state = 'GOOGLbdgsqat'
      const includeGrantedScopes = 'true'
      const responseType = 'token'
      const prompt = 'select_account'

      const uri = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}&state=${state}&include_granted_scopes=${includeGrantedScopes}&response_type=${responseType}&prompt=${prompt}`
      window.location.href = uri
    },
    async initializeAppleSignIn () {
      try {
        const routeData = this.$nuxt.$router.resolve({
          name: 'login'
        })
        const redirectUri = location.origin + routeData.resolved.fullPath
        window.AppleID.auth.init({
          clientId: 'tw.com.kingnetsmart.ecweb',
          scope: 'name email',
          redirectURI: redirectUri,
          state: 'apple-signin-from-ecweb',
          usePopup: true
        })
      } catch (error) {
        console.error('Error initializing Apple Sign In:', error)
      }
    },
    async appleLogin  () {
      const data = await window.AppleID.auth.signIn()
      console.log('appleLogin data', data)
    },
    checkGoogle () {
      const self = this
      if (this.googleInfo.hashParams.access_token) {
        const xhr = new XMLHttpRequest()
        xhr.open('GET',
          'https://www.googleapis.com/oauth2/v2/userinfo')
        xhr.setRequestHeader('Authorization', 'Bearer ' + this.googleInfo.hashParams.access_token)
        xhr.onreadystatechange = function (e) {
          if (xhr.readyState === 4 && xhr.status === 200) {
            const responseObject = JSON.parse(xhr.response)
            self.loginWithGoogle({
              userId: responseObject.id,
              displayName: responseObject.name,
              email: responseObject.email,
              imageUrl: responseObject.picture,
              gender: ''
            })
            // use postMessage to send response to opening window and close or whatever you want to do with it
          } else if (xhr.readyState === 4 && xhr.status === 401) {
            // Token invalid, so prompt for user permission.
            self.oauth2SignIn()
          }
        }
        xhr.send(null)
      }
    },
    async loginWithGoogle (profile) {
      try {
        await this.$store.dispatch('thirdLogin', {
          UserType: 'KingnetGoogle',
          Account: profile.userId,
          IdToken: this.googleInfo.hashParams.access_token,
          DeviceCode: this.$store.state.deviceCode || ''
        })
        console.log('after error')
        if (process.client) {
          this.$auth.$storage.setState('googleEmail', profile.email)
        }
        window.localStorage.setItem(
          'memberUpgradeAlertInfo',
          JSON.stringify({
            time: null,
            open: false
          })
        )
        this.$gaLogEvent('登入', 'Google_成功')
        this.$emit('isLogin')
      } catch (error) {
        console.log('google error', error)
        this.$gaLogEvent('登入', 'Google_失敗')
        this.$nuxt.$router.replace('/auth/login')
        this.$auth.$storage.removeLocalStorage('googleToken')
        this.googleInfo.hasHash = false
        this.compIsLoading = false
      }
    },
    async apiSendRegisterVeriCode () {
      try {
        const response = await this.$axios.post('/api/KingnetAccount/SendRegisterVerifictionCode', { Phone: this.inputData.phone })
        this.submitIsDisabled = false
        return response
      } catch (error) {
        const errorObj = error.response.data.ErrorMessage || {}
        const errorCode = errorObj.Code || null
        const errorReturnObj = {
          isError: false,
          title: '',
          message: '',
          donFun: () => {}
        }
        switch (errorCode) {
          case 400001: // Phone必須是包含加號之E164格式電話號碼(可以是null或空字串),regex: ^09[0-9]{8}$
            break
          case 403000: // 電話錯誤！系統已經綁定過此電話
            if (this.step !== 'login') {
              errorReturnObj.isError = true
              errorReturnObj.title = '此帳號已註冊'
              errorReturnObj.message = '此帳號已註冊，將導至登入流程'
              errorReturnObj.donFun = () => {
                this.alertDialog.open = false
                this.$nuxt.$router.push({ name: 'auth-action', params: { action: 'login' } })
              }
            }
            break
          case 401007: // 5分鐘內最多只能發送3次驗證訊息。
            errorReturnObj.isError = true
            errorReturnObj.title = '簡訊相關錯誤'
            errorReturnObj.message = this.$nuxt.context.env.NODE_ENV_NAME === 'local' ? '5分鐘內最多只能發送3次驗證訊息。' : '24小時內最多只能發送3次驗證訊息。'
            errorReturnObj.donFun = () => {
              this.alertDialog.open = false
            }
            break
          case 401010: // 5分鐘內最多只能發送3次驗證訊息。
            this.$store.dispatch('toast', {
              isOpen: true,
              text: '您已刪除此帳號',
              type: 'error'
            })
            break
          case 406002: // 1分鐘內只能取一次
            this.$store.dispatch('toast', {
              isOpen: true,
              text: '已發送驗證碼，請稍後再嘗試',
              type: 'error'
            })
            break
          default:
            this.$store.dispatch('toast', {
              isOpen: true,
              text: '請稍候再嘗試',
              type: 'error'
            })
            break
        }
        this.alertDialog = {
          ...this.alertDialog,
          ...errorReturnObj,
          open: errorReturnObj.isError
        }
        return Promise.reject(error)
      }
    },
    async apiReSetPasswordVeriCode () {
      try {
        const response = await this.$axios.post('/api/KingnetAccount/SendReSetPasswordVerifictionCode', { Phone: this.inputData.phone })
        this.submitIsDisabled = false
        return response
      } catch (error) {
        const errorObj = error.response.data.ErrorMessage || {}
        const errorCode = errorObj.Code || null
        const errorReturnObj = {
          isError: false,
          title: '',
          message: '',
          donFun: () => {}
        }
        switch (errorCode) {
          case 401007: // 5分鐘內最多只能發送3次驗證訊息。
            errorReturnObj.isError = true
            errorReturnObj.title = '簡訊相關錯誤'
            errorReturnObj.message = this.$nuxt.context.env.NODE_ENV_NAME === 'local' ? '5分鐘內最多只能發送3次驗證訊息。' : '24小時內最多只能發送3次驗證訊息。'
            errorReturnObj.donFun = () => {
              this.alertDialog.open = false
            }
            break
          case 406002: // 1分鐘內只能取一次
            this.$store.dispatch('toast', {
              isOpen: true,
              text: '已發送驗證碼，請稍後再嘗試',
              type: 'error'
            })
            break
          default:
            this.$store.dispatch('toast', {
              isOpen: true,
              text: '請稍候再嘗試',
              type: 'error'
            })
            break
        }
        this.alertDialog = {
          ...this.alertDialog,
          ...errorReturnObj,
          open: errorReturnObj.isError
        }
        this.verification.isSending = false
        return Promise.reject(error)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.login-register-area {
  max-height: 100vh;
  max-height: calc(var(--vh, 1vh) * 100);
  overflow-y: auto;
  .loading {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 300px;
  }
}

header {
  position: relative;
  text-align: center;
  padding-left: var(--safe-area-inset-left);
  padding-right: var(--safe-area-inset-right);
  p {
    margin: 6px auto 0;
    max-width: 315px;
    color: $color-caption;
    font-size: pxTorem(16px);
    line-height: pxTorem(22px);
    &.left {
      text-align: left;
    }
  }
}
.login-register-area__title {
  color: $color-primary;
  font-weight: bold;
}

.login-register-area {
  footer {
    text-align: center;
    .btn {
      height: auto;
      padding: 17px 75px;
      font-size: pxTorem(16px);
      line-height: pxTorem(22px);
    }
    .quick-login__btn {
      border: 1px solid #000;
      height: 40px;
      text-transform: capitalize;
      border-radius: 3px;
      width: 128px;
      font-size: pxTorem(15px);
      line-height: pxTorem(21px);

      &.apple {
        background: #000;
        img {
          filter: invert(1);
        }
        ::v-deep .v-btn__content {
          color: $color-text-inverse;
        }
      }
      &.google {
        border-color: $color-border-primary;
      }
    }

    .login-notice {
      margin-top: 56px;
      font-size: pxTorem(14px);
      color: #000;
      font-weight: 400;
    }
  }
}
.item.stretch {
  align-items: stretch;
}

.quick-login {
  img {
    height: auto;
    width: 24px;
    margin: 8px 8px;
  }

  &__divider {
    position: relative;
    display: grid;
    place-items: center;
    margin: 32px 0 19px;
    &::before {
      z-index: 0;
      content: '';
      position: absolute;
      width: 100%;
      height: 1px;
      background-color: #707070;
    }
  }
  &__divider-text {
    z-index: 0;
    background: white;
    color: #707070;
    padding: 0 10px;
    font-size: pxTorem(15px);
    line-height: pxTorem(21px);
  }

  &__notice {
    font-size: pxTorem(14px);
    margin-top: 16px;
    color: $color-caption;
  }
}

.item {
  &.verifyCode {
    span.label {
      width: 100%;
    }
    .btn {
      margin-left: 20px;
      font-size: pxTorem(16px);
      line-height: pxTorem(22px);
      &.is-sending {
        font-weight: 400;
      }
    }
  }
  & + .item {
    margin-top: 20px;
  }
  &.flex-item {
    $inputHeiight: 56px;
    display: flex;
    flex-wrap: wrap;
    span.label {
      flex: none;
      width: 100%;
      color: $color-text-primary;
      font-size: pxTorem(16px);
      line-height: pxTorem(22px);
    }
    .append-outer {
      flex: none;
      margin-left: 10px;
      height: $inputHeiight;
    }
  }
  .label {
    font-size: pxTorem(17px);
    line-height: pxTorem(24px);
    color: $color-text-primary;
  }
  .append-outer {
    height: auto;
  }
  span.label {
    display: block;
  }
  ::v-deep {
    label.v-label {
      color: $color-caption;
    }

    // reset default style
    .v-input .v-input__control .v-input__slot {
      min-height: 45px;
      padding: 0;
      &>fieldset {
        border-top: none;
        border-left: none;
        border-right: none;
        border-bottom-width: 1px;
        border-radius: 0;
      }

      &>.v-input__append-inner {
        margin: 10px;
        padding: 0;
      }
    }

    .v-text-field__details {
      padding: 0;
    }

    .v-text-field__slot>label {
      top: 14px;
    }
  }
}
.forget-container {
  display: flex;
  justify-content: flex-end;
}
span.forget {
  margin-top: 10px;
  display: inline-block;

  >a {
    color: #3b7fe7;
    font-size: pxTorem(15px);
    line-height: pxTorem(21px);
    text-decoration: none;
    cursor: pointer;
    text-align: right;
  }
}

// 表單送出按鈕 '登入', '註冊', '忘記密碼', '設定密碼'
.submit-btn.v-btn:not(.v-btn--round).v-size--default {
  flex: 1;
  height: 45px;
  font-size: 17px;
}

/** 手機 */
@media screen and (max-width: $mobileWidth) {
  .login-register-area {
    padding: 30px 15px;
  }
  header {
    margin-bottom: 30px;
  }
  .hide-mobile {
    display: none;
  }

  .verifyCode .btn.is-sending {
    max-width: 180px;
  }
}

/** 手機以上 */
@media screen and (min-width: $mobileWidth) {
  header {
    padding-bottom: 15px;
    padding-top: 13px;
  }
  .content {
    padding: 25px 5px;
    padding-left: calc(20px + var(--safe-area-inset-left));
    padding-right: calc(20px + var(--safe-area-inset-right));
  }
  .login-register-area__title {
    display: block;
    color: $color-text-primary;
    font-size: pxTorem(17px);
    line-height: pxTorem(24px);
  }
  .item {
    .label {
      margin-bottom: 4px;
      font-weight: bold;
    }
  }
  .verifyCode .btn {
    height: 45px;
  }
  .item.verifiction {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    span.label {
      flex: none;
    }
    .input {
      display: inline-flex;
      flex: 5;
      margin-right: 10px;
    }
    .btn {
      flex: 4;
      min-height: 56px;
      border-radius: 8px;
    }
  }
}
// Chromium 瀏覽器自動輸入表單 無法觸發資料變更
// 導致 placeholder 與 autofill 內容重疊
// 故以樣式方式隱藏有 autofill 的 placeholder
::v-deep .v-text-field__slot:has(input:-internal-autofill-selected){
  .v-label {
    display: none;
  }
}
</style>
